import React from 'react';
import { motion } from 'framer-motion';
import { Visibility, RocketLaunch, Lightbulb } from '@mui/icons-material';

const AboutVisionMissionValues: React.FC = () => {
  const values = [
    'Integrity',
    'Passion',
    'Innovation',
    'Collaboration'
  ];

  return (
    <section className="flex relative w-full overflow-hidden min-h-0 lg:min-h-screen" style={{ backgroundColor: 'transparent' }}>
      {/* Left Side - Image with Overlay */}
      <div className="hidden lg:block relative self-stretch overflow-hidden" style={{ width: 'calc(40% + 140px)', marginLeft: 0 }}>
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: 'url(/pics/missionvisonetc.JPG)',
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="absolute inset-0 bg-[#EF3F23]/60"></div>
      </div>



      {/* Right Side - Content */}
      <div className="w-full lg:w-[calc(60%-140px)] bg-black py-16 lg:py-24 px-6 sm:px-8 lg:px-12 xl:px-16">
        <div className="max-w-4xl mx-auto space-y-12 lg:space-y-16">
          {/* Our Vision */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:pl-20 relative"
          >
            {/* Vision Icon - positioned at boundary aligned with header */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="hidden lg:flex absolute w-20 h-20 rounded-full bg-[#EF3F23] border-2 border-white items-center justify-center shadow-lg z-10"
              style={{
                left: 'calc(-5rem - 25px)',
                top: '0px'
              }}
            >
              <Visibility sx={{ fontSize: 36, color: 'white' }} />
            </motion.div>
            <h3 
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" 
              style={{ 
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: '-0.05em'
              }}
            >
              Our Vision
            </h3>
            <div className="space-y-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                To be the trusted creative partner for organizations
              </p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                To deliver innovative media solutions that inspire impact
              </p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                To enhance visibility and empower decision-makers
              </p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                To connect with their stakeholders
              </p>
            </div>
          </motion.div>

          {/* Our Mission */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:pl-20 relative"
          >
            {/* Mission Icon - positioned at boundary aligned with header */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex absolute w-20 h-20 rounded-full bg-[#EF3F23] border-2 border-white items-center justify-center shadow-lg z-10"
              style={{
                left: 'calc(-5rem - 25px)',
                top: '0px'
              }}
            >
              <RocketLaunch sx={{ fontSize: 36, color: 'white' }} />
            </motion.div>
            <h3 
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" 
              style={{ 
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: '-0.05em'
              }}
            >
              Our Mission
            </h3>
            <div className="space-y-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                We strive to produce content that captivates audiences' attention
              </p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                We convey our clients' message in a memorable way
              </p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                We make everyone proud
              </p>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                Bizani conveys important information in an entertaining and engaging manner through creativity
              </p>
            </div>
          </motion.div>

          {/* Our Values */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:pl-20 relative"
          >
            {/* Values Icon - positioned at boundary aligned with header */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden lg:flex absolute w-20 h-20 rounded-full bg-[#EF3F23] border-2 border-white items-center justify-center shadow-lg z-10"
              style={{
                left: 'calc(-5rem - 25px)',
                top: '0px'
              }}
            >
              <Lightbulb sx={{ fontSize: 36, color: 'white' }} />
            </motion.div>
            <h3 
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white" 
              style={{ 
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: '-0.05em'
              }}
            >
              Our Values
            </h3>
            <div className="flex flex-col gap-4">
              {values.map((value, index) => {
                return (
                  <motion.div
                    key={value}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  >
                    <h4 
                      className="text-base md:text-lg font-semibold text-gray-300"
                      style={{ 
                        fontFamily: "'Poppins', sans-serif"
                      }}
                    >
                      {value}
                    </h4>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutVisionMissionValues;
